import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  LogOut,
  Loader2,
  User,
  Mail,
  Calendar,
  Clock,
  Activity,
  MapPin,
  Briefcase,
  Building2,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Share2,
  UserPlus,
  Users,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const getStatusBadge = (status) => {
  switch (status) {
    case 'ONBOARDING_COMPLETE':
      return <Badge className="bg-lime-100 text-lime-700">Completed</Badge>;
    case 'ONBOARDING_IN_PROGRESS':
      return <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>;
    case 'EMAIL_VERIFIED_PENDING_ONBOARDING':
      return <Badge className="bg-blue-100 text-blue-700">Pending Onboarding</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const formatDuration = (ms) => {
  if (!ms || ms < 0) return 'N/A';
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
};

const StatCard = ({ label, value, icon: Icon, color = "slate" }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
        color === 'lime' ? 'bg-lime-100' : color === 'blue' ? 'bg-blue-100' : color === 'amber' ? 'bg-amber-100' : 'bg-slate-100'
      }`}>
        <Icon className={`h-5 w-5 ${
          color === 'lime' ? 'text-lime-600' : color === 'blue' ? 'text-blue-600' : color === 'amber' ? 'text-amber-600' : 'text-slate-600'
        }`} />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  </div>
);

const ActivityItem = ({ action, timestamp, details }) => {
  const getIcon = () => {
    switch (action) {
      case 'REGISTERED': return <User className="h-4 w-4" />;
      case 'EMAIL_VERIFIED': return <Mail className="h-4 w-4" />;
      case 'LOGIN': return <Activity className="h-4 w-4" />;
      case 'ONBOARDING_COMPLETE': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };
  
  const getColor = () => {
    switch (action) {
      case 'REGISTERED': return 'bg-blue-100 text-blue-600';
      case 'EMAIL_VERIFIED': return 'bg-lime-100 text-lime-600';
      case 'LOGIN': return 'bg-slate-100 text-slate-600';
      case 'ONBOARDING_COMPLETE': return 'bg-lime-100 text-lime-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="flex items-start gap-3 py-3">
      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${getColor()}`}>
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 text-sm">{details || action}</p>
        <p className="text-xs text-slate-500">
          {timestamp ? new Date(timestamp).toLocaleString() : 'Unknown'}
        </p>
      </div>
    </div>
  );
};

export default function AdminCandidateDetail() {
  const { candidateId } = useParams();
  const { authAxios, logout } = useAuth();
  const navigate = useNavigate();
  
  const [candidate, setCandidate] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCandidate = async () => {
      setLoading(true);
      try {
        const [candidateRes, referralRes] = await Promise.all([
          authAxios.get(`/admin/candidates/${candidateId}`),
          authAxios.get(`/admin/candidates/${candidateId}/referrals`)
        ]);
        setCandidate(candidateRes.data);
        setReferralInfo(referralRes.data);
      } catch (error) {
        console.error('Failed to load candidate:', error);
        toast.error('Failed to load candidate details');
        navigate('/admin/candidates');
      } finally {
        setLoading(false);
      }
    };
    loadCandidate();
  }, [authAxios, candidateId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  if (!candidate) return null;

  // Calculate time metrics
  const createdAt = candidate.created_at ? new Date(candidate.created_at) : null;
  const verifiedAt = candidate.email_verified_at ? new Date(candidate.email_verified_at) : null;
  const completedAt = candidate.onboarding_completed_at ? new Date(candidate.onboarding_completed_at) : null;
  
  const timeToVerify = createdAt && verifiedAt ? verifiedAt - createdAt : null;
  const timeToComplete = verifiedAt && completedAt ? completedAt - verifiedAt : null;
  const totalTime = createdAt && completedAt ? completedAt - createdAt : null;

  const profile = candidate.profile || {};
  const preferences = candidate.preferences || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link 
          to="/admin/candidates" 
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidates
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-evergreen-950 to-evergreen-800 flex items-center justify-center text-white text-xl font-heading">
              {candidate.email.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-heading text-slate-900">
                {profile.first_name && profile.last_name 
                  ? `${profile.first_name} ${profile.last_name}`
                  : candidate.email.split('@')[0]}
              </h1>
              <p className="text-slate-500">{candidate.email}</p>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(candidate.candidate_status)}
                {candidate.is_suspended && (
                  <Badge variant="destructive">Suspended</Badge>
                )}
                {candidate.is_locked && (
                  <Badge className="bg-red-100 text-red-700">Locked</Badge>
                )}
              </div>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigate(`/admin/support?user=${candidate.id}`)}
          >
            Manage User
          </Button>
        </div>

        {/* Onboarding Timeline Stats */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-lime-600" />
              Onboarding Journey
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard 
                label="Time to Verify Email" 
                value={formatDuration(timeToVerify)} 
                icon={Mail}
                color="blue"
              />
              <StatCard 
                label="Time to Complete Profile" 
                value={formatDuration(timeToComplete)} 
                icon={Clock}
                color="amber"
              />
              <StatCard 
                label="Total Onboarding Time" 
                value={formatDuration(totalTime)} 
                icon={CheckCircle}
                color="lime"
              />
              <StatCard 
                label="Profile Completion" 
                value={`${candidate.onboarding_completion_percent || 0}%`} 
                icon={TrendingUp}
                color="slate"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Profile Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Current Role</p>
                  <p className="font-medium text-slate-900">{profile.current_title || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Experience</p>
                  <p className="font-medium text-slate-900">{profile.years_experience || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Location</p>
                  <p className="font-medium text-slate-900">
                    {profile.location_city || profile.location_state 
                      ? `${profile.location_city || ''}, ${profile.location_state || ''}`
                      : 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">LinkedIn</p>
                  <a 
                    href={candidate.linkedin_profile_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium text-lime-600 hover:underline truncate block"
                  >
                    {candidate.linkedin_profile_url ? 'View Profile' : 'Not provided'}
                  </a>
                </div>
              </div>

              {/* Preferences */}
              {(preferences.desired_industries?.length > 0 || preferences.desired_functions?.length > 0) && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h4 className="font-medium text-slate-900 mb-4">Preferences</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {preferences.desired_industries?.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                          <Building2 className="h-4 w-4" /> Industries
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {preferences.desired_industries.map(ind => (
                            <Badge key={ind} variant="secondary" className="text-xs">{ind}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {preferences.desired_functions?.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                          <Briefcase className="h-4 w-4" /> Functions
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {preferences.desired_functions.map(func => (
                            <Badge key={func} variant="secondary" className="text-xs">{func}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Activity className="h-5 w-5 text-slate-600" />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                {(candidate.activity_log || []).slice().reverse().map((activity, index) => (
                  <ActivityItem 
                    key={index}
                    action={activity.action}
                    timestamp={activity.timestamp}
                    details={activity.details}
                  />
                ))}
                {(!candidate.activity_log || candidate.activity_log.length === 0) && (
                  <p className="text-sm text-slate-500 py-4 text-center">No activity recorded</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Account Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">Joined</p>
                <p className="font-medium text-slate-900">
                  {candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Last Login</p>
                <p className="font-medium text-slate-900">
                  {candidate.last_login_at ? new Date(candidate.last_login_at).toLocaleDateString() : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Logins</p>
                <p className="font-medium text-slate-900">{candidate.login_count || 0}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Email Verified</p>
                <p className="font-medium text-slate-900 flex items-center gap-1">
                  {candidate.email_verified ? (
                    <><CheckCircle className="h-4 w-4 text-lime-600" /> Yes</>
                  ) : (
                    <><XCircle className="h-4 w-4 text-red-500" /> No</>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Activity */}
        {referralInfo && (
          <Card className="mt-6 border-lime-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Share2 className="h-5 w-5 text-lime-600" />
                Referral Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Referred By */}
              {referralInfo.referred_by && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium mb-2">Referred By</p>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {referralInfo.referred_by.name || referralInfo.referred_by.email}
                      </p>
                      <p className="text-sm text-slate-500">{referralInfo.referred_by.email}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/admin/candidates/${referralInfo.referred_by.id}`)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Referral Code */}
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-1">Referral Code</p>
                <p className="font-mono text-slate-900 bg-slate-100 px-3 py-2 rounded-lg inline-block">
                  {referralInfo.referral_code || 'Not generated'}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{referralInfo.stats?.total_invites || 0}</p>
                  <p className="text-xs text-slate-500">Invites</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{referralInfo.stats?.signed_up || 0}</p>
                  <p className="text-xs text-slate-500">Signed Up</p>
                </div>
                <div className="text-center p-3 bg-lime-50 rounded-lg">
                  <p className="text-2xl font-bold text-lime-600">{referralInfo.stats?.verified || 0}</p>
                  <p className="text-xs text-slate-500">Verified</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{referralInfo.stats?.onboarded || 0}</p>
                  <p className="text-xs text-slate-500">Onboarded</p>
                </div>
              </div>

              {/* Referrals Made */}
              {referralInfo.made_referrals?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">People Referred ({referralInfo.made_referrals.length})</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {referralInfo.made_referrals.map((ref, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <UserPlus className="h-4 w-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{ref.referred_email}</p>
                            <p className="text-xs text-slate-500">
                              Invited {new Date(ref.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          ref.status === 'ONBOARDED' ? 'bg-lime-100 text-lime-700' :
                          ref.status === 'VERIFIED' ? 'bg-blue-100 text-blue-700' :
                          ref.status === 'SIGNED_UP' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {ref.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!referralInfo.made_referrals || referralInfo.made_referrals.length === 0) && !referralInfo.referred_by && (
                <p className="text-sm text-slate-500 text-center py-4">No referral activity</p>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
